import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { environment } from 'client/environments/environment';

export interface SeoAlternate {
  hreflang: string;   // 'en', 'nl', 'x-default'
  href: string;       // absolute URL
}

export interface SeoPayload {
  title?: string;
  description?: string;        // if omitted, default used
  robots?: string;             // 'index,follow' | 'noindex,follow' | ...
  canonicalUrl?: string;       // absolute URL
  imageUrl?: string;           // absolute URL (OG/Twitter)
  ogType?: string;             // 'website' | 'article' | ...
  ogImageAlt?: string;
  locale?: string;             // 'nl_NL' | 'en_US'
  alternates?: SeoAlternate[]; // hreflang alternates
  prevUrl?: string;            // rel=prev absolute URL
  nextUrl?: string;            // rel=next absolute URL
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly brand = 'Blauwtand';
  private readonly defaultDescription =
    'Publiek job board met actuele vacatures, een duidelijke kandidaat-ervaring en tools voor opdrachtgevers.';
  private readonly defaultOg = {
    type: 'website',
    imageAlt: 'Blauwtand – hero image',
    locale: 'nl_NL',
  } as const;

  constructor(
    private readonly titleSvc: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  /** Main entry: title, meta, canonical, OG/Twitter, hreflang, pagination, robots. */
  updateSeo(payload: SeoPayload = {}): void {
    // 1) Title (+brand)
    const fullTitle = this.buildTitle(payload.title);
    if (fullTitle) {
      this.titleSvc.setTitle(fullTitle);
      this.meta.updateTag({ property: 'og:title', content: fullTitle });
      this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    }

    // 2) Description (~160 chars, word boundary, HTML stripped)
    const desc = this.smartTrim(this.stripHtml(payload.description ?? this.defaultDescription), 160);
    this.meta.updateTag({ name: 'description', content: desc });
    this.meta.updateTag({ property: 'og:description', content: desc });
    this.meta.updateTag({ name: 'twitter:description', content: desc });

    // 3) Robots (default index,follow)
    this.meta.updateTag({ name: 'robots', content: payload.robots ?? 'index,follow' });

    // 4) Canonical (strip tracking params; keep only params whitelisted by the caller).
    // Filter params (q, location, industries) must NOT be whitelisted here or per-route:
    // /vacatures?q=systeembeheerder and a dedicated /systeembeheerder-vacatures landing
    // page would otherwise compete for the same keyword. Keyword intent lives on the
    // dedicated SEO landing pages (/:slug), not on filtered forms of /vacatures — so
    // every filtered form canonicalizes back to /vacatures.
    const canonical = payload.canonicalUrl || this.generateCanonical(undefined, ['page']);
    this.setLink('canonical', canonical);
    this.meta.updateTag({ property: 'og:url', content: canonical });

    // 5) Open Graph & Twitter
    const ogType = payload.ogType ?? this.defaultOg.type;
    const locale = payload.locale ?? this.defaultOg.locale;
    const image = payload.imageUrl || this.defaultOgImage();
    const imageAlt = payload.ogImageAlt ?? this.defaultOg.imageAlt;

    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:site_name', content: this.brand });
    this.meta.updateTag({ property: 'og:locale', content: locale });
    this.meta.updateTag({ property: 'og:image', content: image });
    if (imageAlt) this.meta.updateTag({ property: 'og:image:alt', content: imageAlt });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    // 6) hreflang alternates (+og:locale:alternate)
    this.clearAlternates();
    if (payload.alternates?.length) {
      for (const alt of payload.alternates) {
        this.setAlternate(alt['hreflang'], alt['href']);
        if (alt['hreflang'] !== 'x-default') {
          this.meta.updateTag({ property: 'og:locale:alternate', content: alt['hreflang'].replace('-', '_') });
        }
      }
      if (!payload.alternates.find(a => a['hreflang'] === 'x-default')) {
        this.setAlternate('x-default', canonical);
      }
    }

    // 7) Pagination
    this.setPrevNext(payload.prevUrl, payload.nextUrl);
  }

  /** Absolute canonical from current location or provided pathname. */
  generateCanonical(pathname?: string, allowedParams: string[] = []): string {
    const base = (environment.siteUrl?.replace(/\/$/, '') || this.document.location.origin);
    const url = new URL(pathname ? pathname : this.document.location.href, base);

    // Strip tracking/noise params; keep allowed (e.g., page)
    const keep = new Set(allowedParams.map(p => p.toLowerCase()));
    const toStrip = new Set([
      'utm_source','utm_medium','utm_campaign','utm_term','utm_content',
      'gclid','fbclid','igshid','ref','ref_src','mc_cid','mc_eid','utm_id'
    ]);
    const keys: string[] = Array.from((url.searchParams as any).keys?.() || []);
    for (const key of keys) {
      const lower = key.toLowerCase();
      if (toStrip.has(lower) || !keep.has(lower)) {
        url.searchParams.delete(key);
      }
    }
    url.hash = '';
    // Normalize trailing slash
    if (url.pathname !== '/' && url.pathname.endsWith('/')) {
      url.pathname = url.pathname.replace(/\/+$/, '');
    }
    return url.toString();
  }

  /** JSON-LD helpers */
  addJsonLd(id: string, data: object): void {
    this.removeJsonLd(id);
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', id);
    // Escape markup-significant characters as JSON unicode escapes: no content (including
    // CMS text rendered on the server) can close the script element or open a comment.
    script.text = JSON.stringify(data, null, 2)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026');
    this.document.head.appendChild(script);
  }
  removeJsonLd(id: string): void {
    const el = this.document.head.querySelector(
      `script[type='application/ld+json'][data-seo-jsonld='${id}']`
    );
    if (el) el.parentElement?.removeChild(el);
  }

  /** Internal link tag helpers */
  private setLink(rel: 'canonical' | 'prev' | 'next' | 'alternate', href: string, extra?: Record<string,string>): void {
    if (!href) return;
    const absolute = this.ensureAbsolute(href);
    const selector = `link[rel='${rel}']${extra && (extra as any)['hreflang'] ? `[hreflang='${(extra as any)['hreflang']}']` : ''}`;
    let link = this.document.head.querySelector(selector) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', rel);
      if (extra && (extra as any)['hreflang']) link.setAttribute('hreflang', (extra as any)['hreflang']);
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', absolute);
  }
  private setAlternate(hreflang: string, href: string): void {
    this.setLink('alternate', href, { hreflang });
  }
  private clearAlternates(): void {
    Array.from(this.document.head.querySelectorAll("link[rel='alternate'][hreflang]"))
      .forEach(el => el.parentElement?.removeChild(el));
  }
  /** Update <link rel="prev|next"> without touching title/description/canonical/OG.
   *  Use when only the pagination state changed (e.g. paginator click on /vacatures).
   *  Pass undefined to remove either side. */
  setPrevNext(prev?: string, next?: string): void {
    this.removeLink('prev'); this.removeLink('next');
    if (prev) this.setLink('prev', prev);
    if (next) this.setLink('next', next);
  }
  private removeLink(rel: string): void {
    const el = this.document.head.querySelector(`link[rel='${rel}']`);
    if (el) el.parentElement?.removeChild(el);
  }

  /** Build a brand-suffixed title and avoid double-branding. */
  private buildTitle(pageTitle?: string): string {
    const cleaned = (pageTitle || '').trim();
    if (!cleaned) return this.brand;
    const lowerBrand = this.brand.toLowerCase();
    if (cleaned.toLowerCase().includes(lowerBrand)) return cleaned;
    return `${cleaned} • ${this.brand}`;
  }

  private defaultOgImage(): string {
    const base = environment.siteUrl?.replace(/\/$/, '') || this.document.location.origin;
    return `${base}/assets/blauwtand_main.webp`;
  }

  /** Trim on word boundary up to maxLen; ensures clean snippets. */
  private smartTrim(text: string, maxLen: number): string {
    const t = (text || '').replace(/\s+/g, ' ').trim();
    if (t.length <= maxLen) return t;
    const cut = t.slice(0, maxLen);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:!\-\s]*$/, '') + '…';
  }
  private stripHtml(text?: string): string {
    return (text || '').replace(/<[^>]*>/g, '');
  }
  private ensureAbsolute(href: string): string {
    try {
      const base = environment.siteUrl?.replace(/\/$/, '') || this.document.location.origin;
      return new URL(href, base).toString();
    } catch {
      return href;
    }
  }
}


